import React, { lazy, Suspense, useContext, useRef } from 'react';
import PreloadContext from 'app/hook/common/usePreloader';

// chunkId → load 함수 레지스트리. hydrate 전 SSR 렌더 청크 선로딩(preloadChunks)용.
const CHUNK_REGISTRY = new Map();

/**
 * `@loadable/component` 대체 코드 스플리팅 헬퍼 (SSR-aware).
 * 클라이언트는 React.lazy + Suspense. 서버는 lazy가 suspend(throw)라 못 쓰므로
 * 워밍업 패스에서 로드 promise만 수집 후 다음 패스부터 동기 렌더(server.render.js N-pass 참고).
 *
 * @param {() => Promise<{default: React.ComponentType}>} factory 동적 import 팩토리
 * @param {{chunkId?: string, fallback?: React.ReactNode}} [options]
 *   chunkId: 빌드 플러그인(injectLazyloadChunkId)이 주입하는 manifest 키 — SSR CSS 주입·선로딩용.
 *   fallback: 클라이언트 네비게이션 로딩 중 표시 노드(기본 없음).
 * @returns {React.ComponentType<any>} `.preload()` 포함(loadable 동등).
 */
const lazyload = (factory, { chunkId, fallback = null } = {}) => {
	let LoadedComponent = null; // 해석된 컴포넌트 캐시 (서버/클라 공유)
	let loadPromise = null;

	// 동적 import 실행 (멱등 — 최초 1회만 fetch)
	const load = () => {
		if (!loadPromise) {
			loadPromise = factory().then((mod) => {
				const Component = mod?.default ?? mod;
				LoadedComponent = Component;
				return LoadedComponent;
			});
		}
		return loadPromise;
	};

	if (chunkId) CHUNK_REGISTRY.set(chunkId, load);

	const LazyClient = lazy(() => load().then((Component) => ({ default: Component })));

	const Lazyloaded = (props) => {
		const preloadContext = useContext(PreloadContext);
		// 렌더 경로(동기 vs Suspense)를 mount 시점에 고정. 도중에 바꾸면 엘리먼트 타입이 변해
		// 서브트리가 remount되어 하위 state가 초기화된다(폼 입력값 유실).
		const syncOnMount = useRef(LoadedComponent !== null);

		// 서버(SSR): preloadContext는 서버에서만 존재
		if (preloadContext) {
			if (chunkId) preloadContext.chunks.add(chunkId); // 렌더된 청크 기록(CSS 주입용)
			if (LoadedComponent) return <LoadedComponent {...props} />; // 워밍업 완료 → 동기 렌더
			if (!preloadContext.done) {
				preloadContext.lazy.push(load()); // 워밍업: promise만 수집, 이번 패스는 비움
				return null;
			}
		}

		// 클라이언트, hydrate 직행: preloadChunks가 먼저 로드한 경우 동기 렌더
		// (SSR HTML과 트리 일치 → hydration mismatch 없음)
		if (syncOnMount.current) {
			return <LoadedComponent {...props} />;
		}

		// 클라이언트 네비게이션 진입: Suspense 코드 스플리팅 (resolve 후엔 동기 렌더됨)
		return (
			<Suspense fallback={fallback}>
				<LazyClient {...props} />
			</Suspense>
		);
	};

	Lazyloaded.displayName = chunkId ? `Lazyloaded(${chunkId})` : 'Lazyloaded';
	// loadable 동등의 preload(). 호버/뷰포트 진입 등에서 미리 fetch할 때 사용. 멱등.
	Lazyloaded.preload = load;
	return Lazyloaded;
};

/**
 * SSR이 렌더한 청크들을 hydrate 전에 선로딩(loadableReady 동등).
 * 로드된 청크가 중첩 lazyload를 새로 등록할 수 있어 진행이 없을 때까지 반복.
 * @param {string[]} chunkIds SSR이 주입한 렌더 청크 키 목록(__SSR_CHUNKS__)
 */
export async function preloadChunks(chunkIds) {
	const pending = new Set(Array.isArray(chunkIds) ? chunkIds : []);
	for (let i = 0; i < 12 && pending.size > 0; i += 1) {
		const ready = [...pending].filter((id) => CHUNK_REGISTRY.has(id));
		if (ready.length === 0) break; // 남은 id가 등록될 가능성 없음 → 중단
		// eslint-disable-next-line no-await-in-loop
		await Promise.all(
			ready.map((id) =>
				CHUNK_REGISTRY.get(id)()
					.then(() => pending.delete(id))
					.catch(() => pending.delete(id)),
			),
		);
	}
}

export default lazyload;
